import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, RefreshCw, Zap, Rocket, ArrowRight } from 'lucide-react';

const useCases = [
  {
    icon: TrendingUp,
    title: 'Scaling Up',
    situation: "Your app is growing fast and the current setup can't keep up.",
    solution: 'I migrate you to a containerized setup with auto-scaling, proper caching layers, and monitoring — so growth becomes a feature, not a problem.',
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
  },
  {
    icon: RefreshCw,
    title: 'Legacy Migration',
    situation: 'Stuck on outdated infrastructure that costs too much and moves too slow.',
    solution: 'I plan and execute a near zero-downtime migration to modern cloud-native architecture, keeping your team productive throughout.',
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
  },
  {
    icon: Zap,
    title: 'Performance Issues',
    situation: 'Slow queries, high latency, or unexplained bottlenecks killing user experience.',
    solution: 'I audit your stack, identify the real culprits, and implement targeted fixes.',
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
  },
  {
    icon: Rocket,
    title: 'Starting Up',
    situation: "You're launching a new product and need infrastructure that's solid from day one, not a hack.",
    solution: 'I design and build your initial stack with best practices built-in: proper monitoring, ready-to-scale architecture, and clean deployment pipelines — so you can focus on building features.',
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
  },
];

export function EngagementModels() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <div className="text-center mb-16 space-y-4">
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
          Common Scenarios I Handle
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Every project starts with a challenge. Here's how I typically help.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {useCases.map((useCase) => {
          const Icon = useCase.icon;
          return (
            <div key={useCase.title}>
              <Card className="h-full border-border/50 hover:border-primary/30 transition-all duration-300 group overflow-hidden">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className={`${useCase.bg} w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110`}>
                      <Icon className={`w-6 h-6 ${useCase.color}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-1">{useCase.title}</h3>
                      <p className="text-sm text-muted-foreground italic">"{useCase.situation}"</p>
                    </div>
                  </div>
                  
                  <div className="pl-16">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary mb-2">
                      <ArrowRight className="w-3 h-3" />
                      My Approach
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {useCase.solution}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>

      <div className="mt-12 text-center">
        <p className="text-muted-foreground mb-4">
          Don't see your situation here?
        </p>
        <a
          href="#contact"
          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
        >
          Tell me what you're dealing with
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}

